import { Input, Space, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import "./invitationlisttablecolumn.css";
import { getFullName, TAG_COLOR_INVITE_STATUS, TAG_COLOR_ROLE } from "../../../careprovider/visits/utils";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";
import { performPostRequest } from "../../../../service/network/NetworkService";
import { getApiUrl } from "../../../../service/network/UrlMapper";
import IntentType from "../../../../intentexecutor/utils/IntentType";

const InvitationListTableColumns = ({
  resendInvite,
  revokeInvite,
  editRow,
  saveEditRow,
  cancelEditRow,
  loadingRow,
  editingRowId,
  editEmail,
  editEmailError,
  handleEditEmailChange,
}) => {
  const organizationIdentifier = useGetOrgIdentifier()
  const [alphabetizeByFamilyName, setAlphabetizeByFamilyName] = useState(false)
  const tagColorRole = (role) => TAG_COLOR_ROLE[role];
  const tagColorInviteStatus = (status) => TAG_COLOR_INVITE_STATUS[status];
  const isRowEditing = (record) => editingRowId === record?.userIdentifier;
  const isRowLoading = (record) =>
    Boolean(loadingRow[record?.userIdentifier]);

  const renderActionIcon = ({
    record,
    iconKey,
    tooltipTitle,
    onClick,
    disabled = false,
  }) => {
    const rowLoading = isRowLoading(record);
    const isDisabled = disabled || rowLoading;

    return (
      <Tooltip
        title={rowLoading ? "Processing..." : tooltipTitle}
        placement="bottom"
      >
        <span
          className={`invitation-action-icon-wrapper${
            isDisabled ? " invitation-action-icon-wrapper--disabled" : ""
          }`}
        >
          <SmartIcon
            iconKey={iconKey}
            handleIconClick={isDisabled ? undefined : onClick}
          />
        </span>
      </Tooltip>
    );
  };

    useEffect(() => {
      const getAlphabetizeConfig = async () => {
        const payload = {
          intent: "get_configuration",
          payload: { configurationKey: ["niramaya.alphabetize.workforcelist"] },
          context: { organizationIdentifier }
        };
  
        const result = await performPostRequest(getApiUrl()[IntentType.GET_CONFIGURATION], JSON.stringify(payload));
        const response = await result.json();
        const alphabetizeConfig = JSON.parse(response[0]?.configurationValue || "{}");
  
        setAlphabetizeByFamilyName(alphabetizeConfig?.alphabetizeByFamilyName);
      };
  
      getAlphabetizeConfig();
    }, [organizationIdentifier]);

  const columns = [
    {
      title: "Name",
      dataIndex: "givenName",
      align: "left",
      ellipsis: true,
      sorter: {
				compare: (a, b) => alphabetizeByFamilyName ? a.familyName.localeCompare(b.familyName) : a.givenName.localeCompare(b.givenName),
			},
      render: (text, record) => (
        <span>{getFullName(record?.givenName, record?.familyName)}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      align: "left",
      render: (email, record) =>
        isRowEditing(record) ? (
          <div className="invitation-email-edit">
            <Input
              type="email"
              value={editEmail}
              status={editEmailError ? "error" : undefined}
              onChange={(event) => handleEditEmailChange(event.target.value)}
              placeholder="Email"
              disabled={isRowLoading(record)}
            />
            {editEmailError && (
              <span className="invitation-email-edit-error">
                {editEmailError}
              </span>
            )}
          </div>
        ) : (
          email
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "left",
      render: (status) => {
        return (
          <Tag color={tagColorInviteStatus(status)} className="tag-container">
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Roles",
      dataIndex: "jobTitle",
      align: "left",
      render: (jobTitle) => {
        return (
          <Tag color={tagColorRole(jobTitle)} className="tag-container">
            {jobTitle}
          </Tag>
        );
      },
    },
    {
      title: "Invitation Date",
      dataIndex: "dateCreated",
      width: 170,
      align: "left",
      sorter: (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated),
      render: (dateCreated) => {
        return new Date(dateCreated).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        });
      },
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (text, record) => {
        const rowLoading = isRowLoading(record);
        const isAnotherRowEditing =
          Boolean(editingRowId) && editingRowId !== record?.userIdentifier;

        if (isRowEditing(record)) {
          return (
            <Space size={6} className="patientlist-action-items">
              <PrimaryButton
                buttonText="Save"
                buttonDisable={rowLoading}
                loading={rowLoading}
                handleOnClick={() => saveEditRow(record)}
              />
              <SecondaryButton
                buttonText="Cancel"
                buttonDisable={rowLoading}
                handleOnClick={cancelEditRow}
              />
            </Space>
          );
        }

        return (
          <Space size={10} className="patientlist-action-items">
            {record?.isActive &&
              (record.status === "Invited" ||
                record?.status === "Revoked" ||
                record.status === "Pending Invite") &&
              renderActionIcon({
                record,
                iconKey: "resendInvite",
                tooltipTitle: "Resend Invite",
                onClick: () => resendInvite(record),
              })}
            {record?.isActive &&
              (record.status === "Invited" ||
                record?.status === "Revoked" ||
                record.status === "Pending Invite") &&
              renderActionIcon({
                record,
                iconKey: "edit",
                tooltipTitle: "Edit Email",
                onClick: () => editRow(record),
                disabled: isAnotherRowEditing,
              })}
            {record?.isActive &&
              record.status === "Invited" &&
              renderActionIcon({
                record,
                iconKey: "revoke",
                tooltipTitle: "Revoke Invitation",
                onClick: () => revokeInvite(record),
              })}
          </Space>
        );
      },
    },
  ];

  return { columns };
};

export default InvitationListTableColumns;
